// MaintenanceVendorRequests.jsx
// Component to view and manage maintenance vendor requests

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import { ContentCopy, Delete, CheckCircle, Link as LinkIcon } from "@mui/icons-material";
import useMaintenanceVendorRequests from "../../hooks/useMaintenanceVendorRequests";
import useMaintenanceVendorLinks from "../../hooks/useMaintenanceVendorLinks";
import MaintenanceVendorRequestCard from "./MaintenanceVendorRequestCard";
import ViewingRequestsStyles from "./ViewingRequests.styles";
import { useAuth } from "../../hooks/useAuth";
import { getDatabase, ref, get } from "firebase/database";

const MaintenanceVendorRequests = () => {
  const [vendorRequests, setVendorRequests] = useState([]);
  const [vendorLinks, setVendorLinks] = useState([]);
  const [vendorLinkData, setVendorLinkData] = useState({});
  const [loading, setLoading] = useState(true);
  const [linksLoading, setLinksLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState("approved");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const { currentUser } = useAuth();

  const {
    fetchMaintenanceVendorRequests,
    updateVendorRequestStatus,
    deleteVendorRequest,
  } = useMaintenanceVendorRequests();

  const {
    getAllVendorLinks,
    deleteVendorLink,
  } = useMaintenanceVendorLinks();

  useEffect(() => {
    loadVendorRequests();
    loadVendorLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVendorRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = await fetchMaintenanceVendorRequests();
      setVendorRequests(requests);

      // Fetch extra details from users/{currentUser.uid}/vendorLinks/{linkId}
      const linkDataMap = {};
      if (currentUser) {
        const database = getDatabase();
        const requestsWithLinkId = requests.filter((r) => r.linkId);
        const uid = currentUser.uid;
        const vendorLinksRef = ref(database, `users/${uid}/vendorLinks`);
        const vendorLinksSnap = await get(vendorLinksRef);

        if (vendorLinksSnap.exists()) {
          const allLinks = vendorLinksSnap.val();
          requestsWithLinkId.forEach((request) => {
            const linkId = request.linkId;
            let linkData = allLinks[linkId]
              ? { id: linkId, ...allLinks[linkId] }
              : null;

            if (!linkData) {
              for (const [nodeKey, link] of Object.entries(allLinks)) {
                if (link && (link.linkId === linkId || nodeKey === linkId)) {
                  linkData = { id: nodeKey, ...link };
                  break;
                }
              }
            }
            if (linkData) linkDataMap[linkId] = linkData;
          });
        }
        setVendorLinkData(linkDataMap);
      }
    } catch (err) {
      console.error("Error loading vendor requests:", err);
      setError(err.message || "Failed to load vendor requests.");
    } finally {
      setLoading(false);
    }
  };

  const loadVendorLinks = async () => {
    setLinksLoading(true);
    try {
      const links = await getAllVendorLinks();
      const database = getDatabase();
      const linksWithProperty = await Promise.all(
        links.map(async (link) => {
          try {
            const propertyRef = ref(database, `listings/${link.listingId}`);
            const propertySnap = await get(propertyRef);
            if (propertySnap.exists()) {
              const p = propertySnap.val();
              return {
                ...link,
                propertyAddress: p.address || "Unknown Property",
                propertyCity: p.city || "",
                propertyState: p.state || "",
              };
            }
            return { ...link, propertyAddress: "Unknown Property" };
          } catch (err) {
            console.error("Error fetching property for link:", err);
            return { ...link, propertyAddress: "Unknown Property" };
          }
        })
      );
      setVendorLinks(linksWithProperty);
    } catch (err) {
      console.error("Error loading vendor links:", err);
    } finally {
      setLinksLoading(false);
    }
  };

  // Deny is still available (landlord can manually deny an already-approved request)
  const handleDeny = async (listingId, requestId) => {
    if (!window.confirm("Are you sure you want to deny this vendor request?")) return;
    try {
      await updateVendorRequestStatus(listingId, requestId, "denied");
      setSnack({ open: true, message: "Vendor request denied.", severity: "info" });
      await loadVendorRequests();
    } catch (err) {
      setSnack({ open: true, message: err.message || "Failed to deny request.", severity: "error" });
    }
  };

  const handleDelete = async (listingId, requestId) => {
    if (!window.confirm("Are you sure you want to delete this vendor request? This action cannot be undone.")) return;
    try {
      await deleteVendorRequest(listingId, requestId);
      setSnack({ open: true, message: "Vendor request deleted successfully.", severity: "success" });
      await loadVendorRequests();
    } catch (err) {
      setSnack({ open: true, message: err.message || "Failed to delete request.", severity: "error" });
    }
  };

  const filteredRequests = vendorRequests
    .filter((request) => {
      const status = (request.status || "approved").toLowerCase();
      if (currentTab === "approved") return status === "approved";
      if (currentTab === "denied") return status === "denied";
      return false;
    })
    .sort((a, b) => {
      const timeA = a.submittedAt || a.reviewedAt || 0;
      const timeB = b.submittedAt || b.reviewedAt || 0;
      return timeB - timeA;
    });

  const approvedCount = vendorRequests.filter(
    (r) => (r.status || "approved").toLowerCase() === "approved"
  ).length;
  const deniedCount = vendorRequests.filter(
    (r) => (r.status || "approved").toLowerCase() === "denied"
  ).length;

  const handleCopyLink = async (linkId, linkUrl) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
      setSnack({ open: true, message: "Link copied to clipboard!", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: "Failed to copy link", severity: "error" });
    }
  };

  const handleDeleteLink = async (listingId, linkId) => {
    if (!window.confirm("Are you sure you want to delete this vendor link?")) return;
    try {
      await deleteVendorLink(listingId, linkId);
      setSnack({ open: true, message: "Vendor link deleted successfully.", severity: "success" });
      await loadVendorLinks();
    } catch (err) {
      setSnack({ open: true, message: err.message || "Failed to delete link.", severity: "error" });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    return (
      new Date(timestamp).toLocaleDateString() +
      " " +
      new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <Box sx={ViewingRequestsStyles.container}>
      <Typography variant="h5" sx={ViewingRequestsStyles.title}>
        Maintenance Vendor Requests
      </Typography>

      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label={`Approved (${approvedCount})`} value="approved" />
        <Tab label={`Denied (${deniedCount})`} value="denied" />
        <Tab label={`Generated Links (${vendorLinks.length})`} value="links" />
      </Tabs>

      {currentTab === "links" ? (
        <>
          {linksLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : vendorLinks.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", p: 4 }}>
              No generated vendor links. Generate a link from a property listing to get started.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {vendorLinks.map((link) => {
                const linkUrl = `${window.location.origin}/maintenance-vendor/${link.listingId}/${link.linkId}`;
                return (
                  <Card key={link.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            {link.linkName || "Unnamed Link"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {link.propertyAddress}
                            {link.propertyCity && `, ${link.propertyCity}`}
                            {link.propertyState && `, ${link.propertyState}`}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                            <Chip
                              label={link.status || "active"}
                              size="small"
                              color={link.status === "active" ? "success" : "default"}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Created: {formatDate(link.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {link.slots?.length ? (
                              <><strong>{link.slots.length}</strong> time slot{link.slots.length !== 1 ? "s" : ""} available</>
                            ) : (
                              <>Vendor selects date &amp; 2-hour arrival window</>
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(link.linkId, linkUrl)}
                            color={copiedLinkId === link.linkId ? "success" : "primary"}
                          >
                            {copiedLinkId === link.linkId ? <CheckCircle /> : <ContentCopy />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteLink(link.listingId, link.linkId)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LinkIcon fontSize="small" color="action" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            wordBreak: "break-all",
                            color: "text.secondary",
                          }}
                        >
                          {linkUrl}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </>
      ) : (
        <>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : filteredRequests.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", p: 4 }}>
              No {currentTab} maintenance vendor requests.
            </Typography>
          ) : (
            <Box sx={ViewingRequestsStyles.viewingContainer}>
              {filteredRequests.map((request) => (
                <MaintenanceVendorRequestCard
                  key={request.id}
                  request={request}
                  vendorLinkData={request.linkId ? vendorLinkData[request.linkId] : null}
                  // No onApprove — all requests are auto-approved on submission
                  onDeny={handleDeny}
                  onDelete={handleDelete}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaintenanceVendorRequests;
export { MaintenanceVendorRequests };